/**
 * Async thunks shared by both MyAvailability and TeamAvailability slices.
 * Provides a single source-of-truth for create/update/delete operations to avoid duplication.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { deleteJSON, postJSON, putJSON } from 'api/fetch';

import { toI18n } from 'util/i18n';

import { AvailabilityPayload, CreateAvailabilityPayload } from './types';

export const sharedCreateNewAvailability = (sliceName: string) =>
  createAsyncThunk(
    `${sliceName}/createNewAvailability`,
    async (payload: CreateAvailabilityPayload, { rejectWithValue }) => {
      try {
        return await postJSON('/availabilities', payload);
      } catch (error: any) {
        const responseText =
          (await error.response?.text?.()) || toI18n('errors.generic');
        return rejectWithValue(responseText);
      }
    }
  );

export const sharedEditAvailability = (sliceName: string) =>
  createAsyncThunk(
    `${sliceName}/editAvailability`,
    async (
      payload: AvailabilityPayload & { id: number },
      { rejectWithValue }
    ) => {
      try {
        const { id, ...payloadData } = payload;
        return await putJSON(`/availabilities/${id}`, payloadData);
      } catch (error: any) {
        const responseText =
          (await error.response?.text?.()) || toI18n('errors.generic');
        return rejectWithValue(responseText);
      }
    }
  );

export const sharedDeleteAvailability = (sliceName: string) =>
  createAsyncThunk(
    `${sliceName}/deleteAvailability`,
    async ({ id }: { id: number }, { rejectWithValue }) => {
      try {
        await deleteJSON(`/availabilities/${id}`);
        return { id };
      } catch (error: any) {
        const responseText =
          (await error.response?.text?.()) || toI18n('errors.generic');
        return rejectWithValue(responseText);
      }
    }
  );
